'use client';

/**
 * FormField — accessibility-first wrapper for form inputs.
 *
 * Solves the audit finding that signup-form inputs had zero
 * aria-describedby associations to their error messages. Render-prop API so
 * any input primitive (Input, Textarea, Select, custom) can plug in
 * without re-implementing the a11y wiring.
 *
 * Usage:
 *
 *   <FormField
 *     label="Work email"
 *     hint="We'll send a magic-link sign-in."
 *     error={errors.email}
 *     required
 *   >
 *     {({ id, describedBy, invalid }) => (
 *       <Input
 *         id={id}
 *         aria-describedby={describedBy}
 *         aria-invalid={invalid}
 *         type="email"
 *         required
 *       />
 *     )}
 *   </FormField>
 *
 * Architectural notes:
 *  - Generates stable ids via React's useId so multiple instances coexist.
 *  - aria-describedby contains BOTH hint and error ids (space-separated)
 *    when both are present — screen readers read both.
 *  - aria-invalid is the standard a11y signal for validation failure.
 *  - The required marker (*) is aria-hidden — the underlying input's
 *    `required` attribute carries the meaning for assistive tech.
 *  - Token-based colors (destructive, muted-foreground) so dark mode works.
 */

import * as React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface FormFieldRenderProps {
  /** id to put on the input — already wired up to the Label's htmlFor */
  id: string;
  /** Space-separated ids of the hint and/or error elements. undefined when neither. */
  describedBy: string | undefined;
  /** True when an error is currently displayed. */
  invalid: boolean;
}

export interface FormFieldProps {
  label: string;
  /** Optional helper text shown below the input. */
  hint?: string;
  /** Error message — when present, renders in the destructive style and sets aria-invalid. */
  error?: string;
  /** Visual required marker (also pass `required` to the underlying input). */
  required?: boolean;
  /** Override the auto-generated id (rarely needed). */
  id?: string;
  className?: string;
  children: (props: FormFieldRenderProps) => React.ReactNode;
}

export function FormField({
  label,
  hint,
  error,
  required,
  id: idOverride,
  className,
  children,
}: FormFieldProps) {
  const autoId = React.useId();
  const id = idOverride ?? `field-${autoId}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id} className="flex items-center gap-1">
        <span>{label}</span>
        {required ? (
          <span
            aria-hidden="true"
            data-required-marker
            className="text-destructive"
          >
            *
          </span>
        ) : null}
      </Label>
      {children({ id, describedBy, invalid })}
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
